import { useState, useEffect } from "react";
import {
  Form,
  useLoaderData,
  useActionData,
  useNavigation,
  useNavigate,
  useFetcher,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { useAuth } from "../../hooks/useAuth";
import type { OrganizationSettingsData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import "./OrganizationSettingsPage.css";

export default function OrganizationSettingsPage() {
  const { organization } = useLoaderData() as OrganizationSettingsData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const deleteFetcher = useFetcher();
  const { user, refetchUser } = useAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  const userOrg = user?.organizations.find((o) => o.id === organization.id);
  const userRole = userOrg?.role || null;

  useEffect(() => {
    if (!actionData) return;

    if (actionData.success) {
      toast.success("Organization updated successfully!");
      refetchUser();
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData, refetchUser]);

  useEffect(() => {
    if (deleteFetcher.data) {
      const result = deleteFetcher.data as ActionResult;
      if (result.error) {
        toast.error(result.error);
        setShowDeleteConfirm(false);
      }
    }
  }, [deleteFetcher.data]);

  return (
    <>
      <Header />
      <div className="container">
        <div className="settings-container">
          <div className="settings-header">
            <h1>Organization Settings</h1>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/organizations/${organization.id}`)}
            >
              Back to Organization
            </button>
          </div>

          <div className="settings-section">
            <h2>General Settings</h2>
            <Form method="post" className="settings-form">
              <div className={`form-group ${actionData?.fieldErrors?.name ? "error" : ""}`}>
                <label htmlFor="name">Organization Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  defaultValue={organization.name}
                  placeholder="My Organization"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.name && (
                  <span className="form-error">{actionData.fieldErrors.name}</span>
                )}
              </div>

              <div className={`form-group ${actionData?.fieldErrors?.slug ? "error" : ""}`}>
                <label htmlFor="slug">URL Slug</label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  defaultValue={organization.slug}
                  placeholder="my-organization"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.slug && (
                  <span className="form-error">{actionData.fieldErrors.slug}</span>
                )}
                <small className="help-text">
                  Only lowercase letters, numbers, and hyphens allowed.
                </small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span> Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </Form>
          </div>

          {userRole === "owner" && (
            <div className="settings-section danger-zone">
              <h2>Danger Zone</h2>
              <div className="danger-content">
                <div>
                  <h3>Delete Organization</h3>
                  <p>
                    Once you delete an organization, there is no going back. This will permanently
                    delete the organization, all teams, and all memberships.
                  </p>
                </div>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Organization
                  </button>
                ) : (
                  <div className="delete-confirm">
                    <p className="confirm-text">Are you sure? This action cannot be undone.</p>
                    <div className="confirm-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <deleteFetcher.Form
                        method="post"
                        action={`/organizations/${organization.id}/settings/delete`}
                      >
                        <button type="submit" className="btn-danger" disabled={isDeleting}>
                          {isDeleting ? (
                            <>
                              <span className="loading-spinner"></span> Deleting...
                            </>
                          ) : (
                            "Yes, Delete Forever"
                          )}
                        </button>
                      </deleteFetcher.Form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
